import React, { useEffect, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModernSidebar from '../components/layout/ModernSidebar';
import ModernTopBar from '../components/layout/ModernTopBar';
import useLayout from './hooks/useLayout';
import './MasterTemplate.css';

const GrootChat = lazy(() => import('../components/AI/AIChat'));

/**
 * Master Template - Universal wrapper for all pages
 * Features:
 * - Intelligent sidebar (collapsible, hover-scroll, RTL support)
 * - Modern TopBar (fully bilingual)
 * - Responsive layout (desktop, tablet, mobile)
 * - No overlaps, smooth transitions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 * @param {boolean} props.hideLayout - Hide sidebar/topbar (for special pages like assessment runner)
 * @param {string} props.layoutType - 'app' | 'auth' | 'wizard' | 'minimal'
 */
const MasterTemplate = ({
  children,
  user,
  onLogout,
  hideLayout = false,
  layoutType = 'app',
  variant = 'default'
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar, isMobile } = useLayout();
  // Mobile menu starts closed (hidden)
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  // Ensure sidebar is hidden on mobile by default
  useEffect(() => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);
  
  const isRTL = i18n.language === 'ar';
  
  // Set document direction
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);
  
  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  
  // For full-screen layouts (assessment runner, reports, etc.)
  if (hideLayout || layoutType === 'fullscreen') {
    return (
      <div className={`master-template fullscreen ${isRTL ? 'rtl' : 'ltr'}`}>
        {children}
      </div>
    );
  }
  
  // For auth and minimal layouts (signin, signup, etc.)
  if (layoutType === 'auth' || layoutType === 'minimal') {
    return (
      <div className={`master-template minimal ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="minimal-content">
          {children}
        </div>
      </div>
    );
  }
  
  // Main app layout
  const sidebarWidth = sidebarCollapsed ? 64 : 288;
  const contentMargin = isMobile ? 0 : sidebarWidth;

  return (
    <div className={`master-template app-layout ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-to-main">
        {t('a11y.skipToMainContent', 'Skip to main content')}
      </a>

      {/* Mobile Backdrop */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="mobile-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          onTouchStart={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container */}
      <div className={`sidebar-container ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'} ${isMobile && mobileMenuOpen ? 'mobile-open' : ''}`} data-mobile-open={isMobile && mobileMenuOpen}>
        <ModernSidebar 
          isCollapsed={sidebarCollapsed}
          onToggle={() => {
            if (isMobile) {
              setMobileMenuOpen(!mobileMenuOpen);
            } else {
              toggleSidebar();
            }
          }}
          user={user}
          isMobile={isMobile}
          mobileOpen={mobileMenuOpen}
        />
      </div>

      {/* TopBar */}
      <ModernTopBar 
        user={user}
        onLogout={onLogout}
        onMobileMenuToggle={() => setMobileMenuOpen(prev => !prev)}
        isMobile={isMobile}
        sidebarWidth={sidebarWidth}
      />

      {/* Main Content Area */}
      <main
        id="main-content"
        className="main-content-area"
        style={{ '--sidebar-current-width': `${contentMargin}px` }}
      >
        <div className={`page-content${variant !== 'default' ? ` variant-${variant}` : ''}`}>
          {children}
        </div>
      </main>

      {/* Groot floating chat widget */}
      {user && (
        <Suspense fallback={null}>
          <GrootChat />
        </Suspense>
      )}
    </div>
  );
};

export default MasterTemplate;

